import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { getApiBaseUrl } from '../apiBase';
import Logo from '../components/Logo';
import LandingHeroBackground from '../components/LandingHeroBackground';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { fetchSportTypeCatalog, flattenSportCatalog } from '../api/sportTypes';
import { applySessionPayload } from '../utils/profileSession';
import { setActiveSchool } from '../utils/activeSchool';
import { setAccountRole } from '../utils/roles';

const API = getApiBaseUrl();

const DEMO_ROLES = [
    { key: 'Owner', icon: '🏫', titleKey: 'roleOwner', descKey: 'roleOwnerDesc' },
    { key: 'Coach', icon: '🏋️', titleKey: 'roleCoach', descKey: 'roleCoachDesc' },
    { key: 'Parent', icon: '👨‍👩‍👧', titleKey: 'roleParent', descKey: 'roleParentDesc' },
    { key: 'Student', icon: '🎽', titleKey: 'roleStudent', descKey: 'roleStudentDesc' },
];

const ACCESS_ROLE_BY_DEMO = {
    Owner: 'demo',
    Coach: 'coach',
    Student: 'member',
    Parent: 'member',
};

const NAV_ROLE_BY_DEMO = {
    Owner: 'Owner',
    Coach: 'Coach',
    Student: 'Member',
    Parent: 'Member',
};

function redirectAfterDemoLogin(data, selectedRole) {
    const navRole = NAV_ROLE_BY_DEMO[selectedRole] || data.role || 'Owner';
    applySessionPayload({ ...data, role: navRole });
    setAccountRole(navRole);
    localStorage.setItem('role', navRole);
    localStorage.setItem('isDemoUser', 'true');

    setActiveSchool({
        schoolId: data.demoSchoolId ?? data.schoolId,
        schoolName: data.schoolName ?? '',
        accessRole: ACCESS_ROLE_BY_DEMO[selectedRole] || 'demo',
        isDemoSchool: true,
    });

    window.location.href = '/';
}

export default function DemoLoginPage() {
    const { t, i18n } = useTranslation(['demo', 'common']);
    const [step, setStep] = useState('sport');
    const [sports, setSports] = useState([]);
    const [loadingSports, setLoadingSports] = useState(true);
    const [selectedSport, setSelectedSport] = useState(null);
    const [selectedRole, setSelectedRole] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const loadSports = useCallback(async () => {
        setLoadingSports(true);
        setError('');
        try {
            const groups = await fetchSportTypeCatalog(true);
            const flat = flattenSportCatalog(groups).filter((s) => s.hasDemoSchool !== false);
            setSports(flat);
        } catch {
            setSports([]);
            setError(t('notAvailable'));
        } finally {
            setLoadingSports(false);
        }
    }, [t]);

    useEffect(() => {
        loadSports();
    }, [loadSports, i18n.language]);

    const sportCards = useMemo(
        () => sports.map((sport) => (
            <button
                key={sport.id}
                type="button"
                className={`sp-demo-sport-card${selectedSport?.id === sport.id ? ' sp-demo-sport-card--selected' : ''}`}
                onClick={() => setSelectedSport(sport)}
            >
                <span className="sp-demo-sport-card__emoji">{sport.emoji || '🏆'}</span>
                <span className="sp-demo-sport-card__name">{sport.name}</span>
            </button>
        )),
        [sports, selectedSport]
    );

    async function handleDemoLogin() {
        if (!selectedSport || !selectedRole) return;
        setBusy(true);
        setError('');
        try {
            const res = await axios.post(`${API}/auth/demo-login`, {
                sportTypeId: selectedSport.id,
                role: selectedRole,
                languageCode: i18n.language,
            });
            redirectAfterDemoLogin(res.data, selectedRole);
        } catch (err) {
            setError(err.response?.data?.message || t('loginError'));
            setBusy(false);
        }
    }

    return (
        <div className="sp-auth-page page-login">
            <div className="sp-auth-hero hide-mobile-decor">
                <LandingHeroBackground variant="auth" />
                <div className="sp-auth-hero__content">
                    <Link to="/" style={{ display: 'inline-block', marginBottom: 32 }}>
                        <Logo variant="dark" height={48} />
                    </Link>
                    <p className="sp-auth-hero__tagline">{t('tryDemo')}</p>
                    <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: 15, marginTop: 12 }}>
                        {t('noCreditCard')}
                    </p>
                </div>
            </div>

            <div className="sp-auth-form-panel">
                <div style={{ width: '100%', maxWidth: 480 }}>
                    <div style={{ textAlign: 'center', marginBottom: 24 }} className="hide-desktop-decor">
                        <Link to="/" style={{ display: 'inline-block', margin: '0 auto 24px' }}>
                            <Logo variant="light" height={40} />
                        </Link>
                    </div>

                    <div className="sp-data-card" style={{ padding: 32 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                            <Link to="/login" className="nav-touch" style={{ color: '#64748b', fontSize: 14, textDecoration: 'none' }}>
                                ← {t('back')}
                            </Link>
                            <LanguageSwitcher variant="inline" />
                        </div>

                        {step === 'sport' ? (
                            <>
                                <h1 style={{ margin: '0 0 8px', fontSize: 22, fontWeight: 700 }}>{t('selectSport')}</h1>
                                <p style={{ margin: '0 0 20px', color: '#64748b', fontSize: 14 }}>{t('noCreditCard')}</p>
                                {loadingSports ? (
                                    <p style={{ textAlign: 'center', color: '#64748b' }}>{t('loading')}</p>
                                ) : (
                                    <div className="sp-demo-sport-grid">{sportCards}</div>
                                )}
                                <button
                                    type="button"
                                    className="sp-btn sp-btn--primary form-submit"
                                    style={{ width: '100%', marginTop: 24 }}
                                    disabled={!selectedSport || loadingSports}
                                    onClick={() => setStep('role')}
                                >
                                    {t('continue')} →
                                </button>
                            </>
                        ) : (
                            <>
                                <h1 style={{ margin: '0 0 20px', fontSize: 22, fontWeight: 700 }}>{t('selectRole')}</h1>
                                <div className="sp-demo-role-list">
                                    {DEMO_ROLES.map((role) => (
                                        <button
                                            key={role.key}
                                            type="button"
                                            className={`sp-demo-role-card${selectedRole === role.key ? ' sp-demo-role-card--selected' : ''}`}
                                            onClick={() => setSelectedRole(role.key)}
                                        >
                                            <span className="sp-demo-role-card__icon">{role.icon}</span>
                                            <span className="sp-demo-role-card__text">
                                                <span className="sp-demo-role-card__title">{t(role.titleKey)}</span>
                                                <span className="sp-demo-role-card__desc">{t(role.descKey)}</span>
                                            </span>
                                        </button>
                                    ))}
                                </div>
                                <button
                                    type="button"
                                    className="sp-btn sp-btn--primary form-submit"
                                    style={{ width: '100%', marginTop: 24 }}
                                    disabled={!selectedRole || busy}
                                    onClick={handleDemoLogin}
                                >
                                    {busy ? t('loading') : t('tryDemo')}
                                </button>
                                <button
                                    type="button"
                                    className="sp-btn sp-btn--secondary"
                                    style={{ width: '100%', marginTop: 12 }}
                                    disabled={busy}
                                    onClick={() => setStep('sport')}
                                >
                                    {t('selectSport')}
                                </button>
                            </>
                        )}

                        {error && (
                            <div style={{
                                background: '#fce4ec', color: '#c62828', padding: '10px 14px',
                                borderRadius: 8, fontSize: 13, marginTop: 16
                            }}>
                                {error}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
